import { Container, Rectangle, Sprite, Text } from 'pixi.js';
import { gameConfig } from '../config/gameConfig.js';
import { formatCurrencyDigits } from '../lib/formatCurrency.js';

const T = gameConfig.theme;

/** Quick-bet / chip labels: two decimals below 100; from 100 up, no fraction. */
export function formatQuickBetAmount(n, precision = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0.00';
  if (Number.isFinite(precision) && precision !== 2) {
    return formatCurrencyDigits(v, precision);
  }
  const abs = Math.abs(v);
  if (abs >= 100) {
    return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const FORMATTED_AMOUNT_SMALL_FONT_FROM_DIGITS = 5;

function digitCountForAmountFont(formatted) {
  const compact = String(formatted ?? '').replace(/[\s,]/g, '');
  const withoutTrailingCents = /\.00$/.test(compact) ? compact.slice(0, -3) : compact;
  return withoutTrailingCents.replace(/\D/g, '').length;
}

/** Used by quick-bet chips and autoplay round labels when the string is numeric-ish. */
export function fontSizeWhenFormattedLong(formatted, normalPx, smallPx) {
  return digitCountForAmountFont(formatted) >= FORMATTED_AMOUNT_SMALL_FONT_FROM_DIGITS ? smallPx : normalPx;
}

/**
 * Fit quick-bet chip art in the cell; stretch to cellW×cellH so wider cells become rectangles.
 * @param {Sprite} sprite
 * @param {Texture} tex
 * @param {number} cellW
 * @param {number} cellH
 */
export function layoutQuickBetChipSpriteInCell(sprite, tex, cellW, cellH) {
  sprite.width = cellW;
  sprite.height = cellH;
  sprite.x = 0;
  sprite.y = 0;
}

/** Wider-than-tall design rect; bg stretches to this aspect inside each chip cell. */
const REF = { w: 128, h: 96 };

/** One structure only (logical px in REF space). */
const CHIP_DESIGN = {
  valueFontN: 24,
  valueFontS: 24,
  currencyAmountGap: 2,
  bottom: { fontSize: 16 },
  /** Gap between the FUN+amount row and the BET label (REF px). */
  labelGap: 0,
  /** Chip frame art is heavier on the left; nudge copy right so it looks centered. */
  textOpticalXNudge: 2,
};

/** Place `obj` so the horizontal center of its local bounds sits on `centerX`. */
function centerBoundsX(obj, centerX) {
  const b = obj.getLocalBounds();
  obj.x = centerX - b.x - b.width / 2;
}

/** Readable drop shadow for money text on brown/gold HUD panels. */
function hudValueDropShadow() {
  return {
    alpha: 0.9,
    angle: Math.PI / 2,
    blur: 3,
    color: T.hudValueShadow,
    distance: 1,
  };
}

function chipTextFills(selected) {
  const shadow = hudValueDropShadow();
  return {
    fun: selected ? T.hudCurrency : 0xffffff,
    value: selected ? T.hudValue : 0xffffff,
    bottom: T.hudCurrency,
    shadow,
  };
}

/**
 * Quick-bet chip: textured bg + FUN + amount + bottom label. One internal layout; only `cellW` / `cellH` change — everything scales together.
 *
 * @param {object} p
 * @param {number} p.betValue
 * @param {boolean} p.selected
 * @param {import('pixi.js').Texture} p.textureNormal
 * @param {import('pixi.js').Texture} p.textureSelected
 * @param {number} p.cellW
 * @param {number} p.cellH
 * @param {string} [p.bottomText='BET']
 * @param {string} [p.currencyLabel='FUN']
 * @param {number} [p.currencyPrecision=2]
 * @param {string} [p.fontFamily]
 */
export function createQuickBetChipPixi(p) {
  const fontFamily = p.fontFamily ?? '"Montserrat", "Arial", sans-serif';
  const D = CHIP_DESIGN;
  const bottomText = p.bottomText ?? 'BET';
  const currencyLabel = p.currencyLabel ?? 'FUN';
  const selected = !!p.selected;
  const tex = selected ? p.textureSelected : p.textureNormal;

  const outer = new Container();
  const inner = new Container();

  const bg = new Sprite(tex);
  bg.eventMode = 'none';
  layoutQuickBetChipSpriteInCell(bg, tex, REF.w, REF.h);

  const fills = chipTextFills(selected);
  const centerX = REF.w / 2;
  const centerY = REF.h / 2;
  const amtStr = formatQuickBetAmount(p.betValue, p.currencyPrecision ?? 2);
  const valueFontSize = fontSizeWhenFormattedLong(amtStr, D.valueFontN, D.valueFontS);

  const fun = new Text({
    text: currencyLabel,
    style: {
      fontFamily,
      fontSize: valueFontSize,
      fontWeight: '700',
      fill: fills.fun,
      dropShadow: fills.shadow,
    },
  });
  fun.anchor.set(0, 0.5);
  fun.eventMode = 'none';

  const amt = new Text({
    text: amtStr,
    style: {
      fontFamily,
      fontSize: valueFontSize,
      fontWeight: '700',
      fill: fills.value,
      dropShadow: fills.shadow,
    },
  });
  amt.anchor.set(0, 0.5);
  amt.eventMode = 'none';

  const amountRow = new Container();
  amountRow.eventMode = 'none';
  amountRow.addChild(fun, amt);

  const bottom = bottomText
    ? new Text({
        text: bottomText,
        style: {
          fontFamily,
          fontSize: D.bottom.fontSize,
          fontWeight: '700',
          fill: fills.bottom,
          dropShadow: fills.shadow,
        },
      })
    : null;
  if (bottom) {
    bottom.anchor.set(0, 0);
    bottom.eventMode = 'none';
  }

  const contentStack = new Container();
  contentStack.eventMode = 'none';
  contentStack.addChild(amountRow);
  if (bottom) contentStack.addChild(bottom);

  const layoutContentStack = () => {
    const rowMidY = valueFontSize / 2;
    fun.x = 0;
    fun.y = rowMidY;
    amt.x = fun.width + D.currencyAmountGap;
    amt.y = rowMidY;

    const textCenterX = centerX + (D.textOpticalXNudge ?? 0);
    centerBoundsX(amountRow, textCenterX);

    if (bottom) {
      const amountBounds = amountRow.getLocalBounds();
      const amountCenterX = amountRow.x + amountBounds.x + amountBounds.width / 2;
      centerBoundsX(bottom, amountCenterX);
      bottom.y = amountRow.y + amountBounds.y + amountBounds.height + D.labelGap;
    }

    const stackBounds = contentStack.getLocalBounds();
    contentStack.y = centerY - stackBounds.y - stackBounds.height / 2;
  };
  layoutContentStack();

  inner.addChild(bg, contentStack);

  const scale = Math.min(p.cellW / REF.w, p.cellH / REF.h);
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
  inner.scale.set(s);
  inner.position.set((p.cellW - REF.w * s) / 2, (p.cellH - REF.h * s) / 2);

  outer.addChild(inner);
  outer.eventMode = 'static';
  outer.cursor = 'pointer';
  outer.hitArea = new Rectangle(0, 0, p.cellW, p.cellH);

  return outer;
}
